import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useCopyTemplateToGroups, useTemplateCopyTargets } from '@/hooks/useTemplatesData';
import { ApiError } from '@/providers/ApiConfigProvider';
import type { Template } from '@/types/template';

interface CopyTemplateToGroupsDialogProps {
  template: Template | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CopyTemplateToGroupsDialog({ template, open, onOpenChange }: CopyTemplateToGroupsDialogProps) {
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const { data: targets = [], isLoading } = useTemplateCopyTargets(open);
  const copyMutation = useCopyTemplateToGroups();

  const toggleGroup = (companyGroupId: number, checked: boolean) => {
    setSelectedGroupIds((previous) =>
      checked ? [...previous, companyGroupId] : previous.filter((id) => id !== companyGroupId),
    );
  };

  const close = () => {
    setSelectedGroupIds([]);
    onOpenChange(false);
  };

  const handleCopy = () => {
    if (!template || selectedGroupIds.length === 0) return;

    copyMutation.mutate(
      { templateId: template.id, companyGroupIds: selectedGroupIds },
      {
        onSuccess: (response) => {
          const copied = response.results.filter((result) => result.copied).length;
          const skipped = response.results.filter((result) => !result.copied);

          if (copied > 0) {
            toast.success(`'${template.name}' copied to ${copied} company group${copied === 1 ? '' : 's'}`);
          }

          for (const result of skipped) {
            const groupName =
              targets.find((target) => target.companyGroupId === result.companyGroupId)?.name ??
              `group ${result.companyGroupId}`;
            toast.warning(`${groupName}: ${result.skipReason}`);
          }

          close();
        },
        onError: (error) => {
          const message =
            error instanceof ApiError ? error.message : 'An unexpected error occurred while copying the template.';
          toast.error(message);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy to other company groups</DialogTitle>
          <DialogDescription>
            {template
              ? `'${template.name}' will be copied as a new, independent template. Later edits here will not affect the copies.`
              : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {isLoading && <p className="text-sm text-muted-foreground">Loading company groups…</p>}
          {!isLoading && targets.length === 0 && (
            <p className="text-sm text-muted-foreground">There are no other company groups you can copy into.</p>
          )}
          {targets.map((target) => (
            <div key={target.companyGroupId} className="flex items-center gap-3">
              <Checkbox
                id={`copy-target-${target.companyGroupId}`}
                checked={selectedGroupIds.includes(target.companyGroupId)}
                onCheckedChange={(checked) => toggleGroup(target.companyGroupId, checked === true)}
              />
              <Label htmlFor={`copy-target-${target.companyGroupId}`} className="font-normal">
                {target.name}
                <span className="text-muted-foreground ml-2 text-xs">
                  {target.companyCount} compan{target.companyCount === 1 ? 'y' : 'ies'}
                </span>
              </Label>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button onClick={handleCopy} disabled={selectedGroupIds.length === 0 || copyMutation.isPending}>
            {copyMutation.isPending ? 'Copying…' : 'Copy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
